import {DataStatus, Dropdown, DropdownDivider, DropdownItem, IconButton} from 'platform/components';
import {Show} from 'platform/foundation';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {useGetServiceJobActionsQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {JobAction} from '../types/JobAction';

interface JobActionsMenuProps extends TestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  serviceJobId: string;
  actions?: JobAction[];
  isDisabled?: boolean;
}

export function JobActionsMenu(props: JobActionsMenuProps) {
  const [isRequested, setIsRequested] = useState(false);
  const {currentData, isFetching, isUninitialized, isError, refetch} = useGetServiceJobActionsQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
      serviceJobId: props.serviceJobId,
    },
    {skip: !isRequested || props.isDisabled}
  );

  const requestActions = () => {
    if (!props.isDisabled) {
      setIsRequested(true);
    }
  };
  const options = currentData?.jobActions?.options?.filter(isNotNil) ?? [];

  const hasAvailableActions = options.some(
    (option) =>
      option.type === 'list-item' &&
      option.style !== 'hidden' &&
      props.actions?.some((action) => action.key === option.key)
  );

  return (
    <span onMouseEnter={requestActions} onFocus={requestActions}>
      <Dropdown
        data-testid={suffixTestId('actions', props)}
        onOpen={requestActions}
        isDisabled={props.isDisabled}
        dropdownControl={
          <IconButton
            icon="navigation/more_vert"
            aria-label={i18n.t('entity.orderRequest.actions.moreActions')}
            size="small"
            isDisabled={props.isDisabled}
            data-testid={suffixTestId('more-actions', props)}
          />
        }
      >
        <DataStatus
          // Gating on isFetching alone would unmount an open menu whenever a sibling mutation
          // invalidates the bare serviceJobActions tag.
          isLoading={isUninitialized || (isFetching && isNil(currentData))}
          isError={isError && !isFetching}
          isEmpty={!hasAvailableActions}
          errorAction={{title: i18n.t('general.actions.retry'), onClick: () => void refetch()}}
          spacing={4}
          data-testid={suffixTestId('actions-status', props)}
        >
          {options.map((option, index) => {
            const action = props.actions?.find((candidate) => candidate.key === option.key);
            return (
              <Show when={option.style !== 'hidden'} key={option.key}>
                <Show when={option.type === 'list-item' ? action : null}>
                  {(availableAction) => (
                    <DropdownItem
                      {...availableAction}
                      isDisabled={option.style === 'disabled'}
                      data-testid={suffixTestId(`action-[${index}]`, props)}
                    />
                  )}
                </Show>
                <Show when={option.type === 'divider'}>
                  <DropdownDivider />
                </Show>
              </Show>
            );
          })}
        </DataStatus>
      </Dropdown>
    </span>
  );
}
